import React, { useState, useRef } from 'react'
import { ArrowRight, Network, Lock, Zap, ChevronDown, Globe, Shield, Sparkles, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from '@/components/ui/use-toast'

// --- Component Prop Interfaces --- 
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface UseCaseCardProps {
  title: string;
  description: string;
  tag: string;
}
// --- End Component Prop Interfaces ---

const MetanetDesktop: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'nodejs' | 'browser' | 'react'>('nodejs')
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const featuresRef = useRef<HTMLDivElement>(null)
  
  // Handle copy code to clipboard
  const handleCopyCode = () => {
    const codeElements: { [key in 'nodejs' | 'browser' | 'react']: string } = {
      'nodejs': `// Import the Metanet Desktop library
const { MetanetDesktop } = require('@babbage/metanet-client');

// Initialize the client
const client = new MetanetDesktop();

// Create a new identity
async function createIdentity() {
  const identity = await client.createIdentity();
  console.log('Created identity:', identity);
  return identity;
}

// Publish data to MetaNet
async function publishData(identity, data) {
  try {
    const result = await client.publish({
      identity,
      data,
      type: 'text/plain'
    });
    console.log('Published successfully:', result);
    return result;
  } catch (error) {
    console.error('Failed to publish:', error);
    throw error;
  }
}`,
      'browser': `// Import the Metanet Desktop library
import { MetanetDesktop } from '@babbage/metanet-client';

// Initialize the client
const client = new MetanetDesktop();

// Create a new identity
const identity = await client.createIdentity();

// Store the identity for later use
localStorage.setItem('metanet-identity', JSON.stringify(identity));

// Publish data to MetaNet
async function publishToMetaNet(data) {
  try {
    const identity = JSON.parse(localStorage.getItem('metanet-identity'));
    const result = await client.publish({
      identity,
      data,
      type: 'text/plain'
    });
    return { success: true, result };
  } catch (error) {
    console.error('Error publishing to MetaNet:', error);
    return { success: false, error };
  }
}`,
      'react': `import React, { useState } from 'react';
import { MetanetDesktop } from '@babbage/metanet-client';

const MetaNetExample = () => {
  const [identity, setIdentity] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePublish = async () => {
    try {
      setLoading(true);
      const client = new MetanetDesktop();

      // Create an identity if we don't have one
      const currentIdentity = identity || await client.createIdentity();
      if (!identity) setIdentity(currentIdentity);

      // Publish data to MetaNet
      const publishResult = await client.publish({
        identity: currentIdentity,
        data: 'Hello from React!',
        type: 'text/plain'
      });

      setResult(publishResult);
    } catch (error) {
      console.error('Error:', error);
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  }; 

  return (
    <div className="p-4 border rounded">
      <h3 className="text-lg font-medium mb-4">MetaNet Publisher</h3>

      <button
        onClick={handlePublish}
        disabled={loading}
        className="px-4 py-2 bg-primary text-white rounded"
      >
        {loading ? 'Publishing...' : 'Publish to MetaNet'}
      </button>

      {result && (
        <div className="mt-4 p-3 bg-gray-100 rounded">
          {result.error ? (
            <div className="text-red-500">Error: {result.error}</div>
          ) : (
            <div className="text-green-500">Published successfully!</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetaNetExample;`
    };

    const codeToCopy = codeElements[activeTab];
    navigator.clipboard.writeText(codeToCopy).then(() => {
      setIsCopied(true);
      toast({
        title: "Code copied!",
        description: "The code has been copied to your clipboard.",
      });
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  // Handle scrolling to features section
  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-background to-background/80 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-70"></div>
        <div className="absolute -right-32 -top-32 h-96 w-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -left-32 -bottom-32 h-96 w-96 bg-primary/10 rounded-full blur-3xl"></div>
        
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-primary/10 text-primary mb-6">
              <span className="font-medium">Official Release</span>
            </div>
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500">
              Metanet Desktop
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
            Seamless, user-friendly software enabling consumer access to the Metanet experience.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button size="lg" className="group" onClick={() => window.open('/docs/metanet-desktop/quick-start', '_blank')}>
                Quick Start 
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => window.open('https://github.com/babbage-institute/metanet-desktop', '_blank')}>
                View on GitHub
              </Button>
            </div>
            
            <div className="mt-16 flex justify-center">
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center gap-2 text-muted-foreground animate-bounce"
                onClick={scrollToFeatures}
              >
                Explore Features
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Code Example Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Simple to integrate, powerful to use</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Metanet Desktop provides a streamlined developer experience with support for multiple platforms
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Tabs 
              defaultValue="nodejs" 
              onValueChange={(value) => setActiveTab(value as 'nodejs' | 'browser' | 'react')} 
              className="w-full"
            >
              <div className="flex justify-center mb-6">
                <TabsList className="grid grid-cols-3">
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="browser">Browser</TabsTrigger>
                  <TabsTrigger value="react">React</TabsTrigger>
                </TabsList>
              </div>
              
              <div className="rounded-lg overflow-hidden bg-black/90 shadow-xl">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div className="flex gap-1">
                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-gray-400">{activeTab === 'nodejs' ? 'index.js' : activeTab === 'browser' ? 'script.js' : 'App.jsx'}</div>
                </div>
                
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <TabsContent value="nodejs" className="m-0">
                      <pre className="language-javascript p-4 text-sm overflow-auto text-gray-300">
                        <code>{`
// Import the Metanet Desktop library
const { MetanetDesktop } = require('@babbage/metanet-client');

// Initialize the client
const client = new MetanetDesktop();

// Create a new identity
async function createIdentity() {
  const identity = await client.createIdentity();
  console.log('Created identity:', identity);
  return identity;
}

// Publish data to MetaNet
async function publishData(identity, data) {
  try {
    const result = await client.publish({
      identity,
      data,
      type: 'text/plain'
    });
    console.log('Published successfully:', result);
    return result;
  } catch (error) {
    console.error('Failed to publish:', error);
    throw error;
  }
}
                        `}</code>
                      </pre>
                    </TabsContent>
                    
                    <TabsContent value="browser" className="m-0">
                      <pre className="language-javascript p-4 text-sm overflow-auto text-gray-300">
                        <code>{`
// Import the Metanet Desktop library
import { MetanetDesktop } from '@babbage/metanet-client';

// Initialize the client
const client = new MetanetDesktop();

// Create a new identity
const identity = await client.createIdentity();

// Store the identity for later use
localStorage.setItem('metanet-identity', JSON.stringify(identity));

// Publish data to MetaNet
async function publishToMetaNet(data) {
  try {
    const identity = JSON.parse(localStorage.getItem('metanet-identity'));
    const result = await client.publish({
      identity,
      data,
      type: 'text/plain'
    });
    return { success: true, result };
  } catch (error) {
    console.error('Error publishing to MetaNet:', error);
    return { success: false, error };
  }
}
                        `}</code>
                      </pre>
                    </TabsContent>
                    
                    <TabsContent value="react" className="m-0">
                      <pre className="language-jsx p-4 text-sm overflow-auto text-gray-300">
                        <code>{`
import React, { useState } from 'react';
import { MetanetDesktop } from '@babbage/metanet-client';

const MetaNetExample = () => {
  const [identity, setIdentity] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePublish = async () => {
    try {
      setLoading(true);
      const client = new MetanetDesktop();

      // Create an identity if we don't have one
      const currentIdentity = identity || await client.createIdentity();
      if (!identity) setIdentity(currentIdentity);

      // Publish data to MetaNet
      const publishResult = await client.publish({
        identity: currentIdentity,
        data: 'Hello from React!',
        type: 'text/plain'
      });

      setResult(publishResult);
    } catch (error) {
      console.error('Error:', error);
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  }; 

  return (
    <div className="p-4 border rounded">
      <h3 className="text-lg font-medium mb-4">MetaNet Publisher</h3>

      <button
        onClick={handlePublish}
        disabled={loading}
        className="px-4 py-2 bg-primary text-white rounded"
      >
        {loading ? 'Publishing...' : 'Publish to MetaNet'}
      </button>

      {result && (
        <div className="mt-4 p-3 bg-gray-100 rounded">
          {result.error ? (
            <div className="text-red-500">Error: {result.error}</div>
          ) : (
            <div className="text-green-500">Published successfully!</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetaNetExample;
                        `}</code>
                      </pre>
                    </TabsContent>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              <div className="mt-6 flex justify-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyCode}
                  className="flex items-center gap-2"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-4 w-4" /> Copied!
                    </>
                  ) : (
                    <>
                      Copy Code <ClipboardIcon className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Everything you need to build on MetaNet</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Comprehensive toolkit designed for developers of all experience levels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Network className="h-8 w-8 text-primary" />}
              title="MetaNet Protocol"
              description="First-class support for all MetaNet protocol features with a simplified API"
            />
            <FeatureCard
              icon={<Lock className="h-8 w-8 text-primary" />}
              title="Identity Management"
              description="Create, manage and authenticate with Bitcoin-powered digital identities"
            />
            <FeatureCard
              icon={<Globe className="h-8 w-8 text-primary" />}
              title="Network Interoperability"
              description="Seamlessly connect with other MetaNet services and applications"
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8 text-primary" />}
              title="Robust Security"
              description="Built-in encryption and secure authentication mechanisms"
            />
            <FeatureCard
              icon={<Zap className="h-8 w-8 text-primary" />}
              title="High Performance"
              description="Optimized for speed and efficiency, even with large data transactions"
            />
            <FeatureCard
              icon={<Sparkles className="h-8 w-8 text-primary" />}
              title="Extensible Design"
              description="Flexible plugin architecture to extend functionality for your specific needs"
            />
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="py-20 bg-gradient-to-r from-background to-background/90 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold">Try It Yourself</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Experience the Metanet Desktop with this interactive demo
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-6">Metanet Desktop Demo</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Message to publish</label>
                  <textarea 
                    className="w-full border rounded-md p-2 bg-background" 
                    rows={3} 
                    placeholder="Enter a message to publish to the MetaNet..."
                    defaultValue="Hello, MetaNet!"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Content type</label>
                    <select className="w-full border rounded-md p-2 bg-background">
                      <option value="text/plain">Plain Text</option>
                      <option value="application/json">JSON</option>
                      <option value="text/markdown">Markdown</option>
                      <option value="text/html">HTML</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Encryption</label>
                    <select className="w-full border rounded-md p-2 bg-background">
                      <option value="none">None</option>
                      <option value="basic">Basic</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button 
                    className="w-full group"
                    onClick={() => {
                      toast({
                        title: "Demo Mode",
                        description: "In a real application, this would publish your data to the MetaNet. This is just a demonstration.",
                      });
                      
                      // Show success animation after a delay
                      setTimeout(() => {
                        const resultElement = document.getElementById('demo-result');
                        if (resultElement) {
                          resultElement.style.display = 'block';
                        }
                      }, 1000);
                    }}
                  >
                    Publish to MetaNet
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  
                  <div id="demo-result" className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800 hidden">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Successfully published!</h3>
                        <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                          <p>Transaction ID: bsv1:cf2fa1e8b92c3fee1dc5a3e512e303612e0d31cb0987269c7bc20709f45d921e</p>
                          <div className="mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open('https://whatsonchain.com/tx/cf2fa1e8b92c3fee1dc5a3e512e303612e0d31cb0987269c7bc20709f45d921e', '_blank')}
                            >
                              View on Explorer
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Real-world Applications</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              See how developers are using Metanet Desktop to build innovative solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <UseCaseCard
              title="Content Publishing Platform"
              description="Build a decentralized content platform where creators maintain ownership of their work"
              tag="Media"
            />
            <UseCaseCard
              title="Secure Identity System"
              description="Implement user authentication without passwords or centralized identity providers"
              tag="Security"
            />
            <UseCaseCard
              title="Supply Chain Tracking"
              description="Create transparent and immutable record of product journeys from factory to consumer"
              tag="Enterprise"
            />
            <UseCaseCard
              title="Microtransaction Services"
              description="Enable new business models with seamless micropayments for content and services"
              tag="Payments"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>What is Metanet Desktop?</AccordionTrigger>
              <AccordionContent>
                Metanet Desktop is a comprehensive toolkit for building applications that interact with the MetaNet protocol. It simplifies working with Bitcoin-powered backends, identity management, and data storage.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>How does it differ from the Babbage SDK?</AccordionTrigger>
              <AccordionContent>
                While the Babbage SDK provides a broader set of tools for Bitcoin application development, Metanet Desktop is specifically focused on the MetaNet protocol. It offers a more streamlined and purpose-built API for MetaNet interactions.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Is Metanet Desktop suitable for production use?</AccordionTrigger>
              <AccordionContent>
                Yes, Metanet Desktop is designed for production applications with enterprise-grade security and performance. It's being used in real-world applications across various industries.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>What platforms does it support?</AccordionTrigger>
              <AccordionContent>
                Metanet Desktop supports Node.js servers, modern browsers, and various JavaScript frameworks including React, Vue, and Angular.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Why Choose Metanet Desktop?</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              See how Metanet Desktop compares to other solutions
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="py-4 px-6 text-left font-medium min-w-[200px]">Features</th>
                  <th className="py-4 px-6 text-center font-medium min-w-[200px]">
                    <div className="flex flex-col items-center">
                      <span className="text-primary font-bold">Metanet Desktop</span>
                      <span className="text-xs text-muted-foreground">Our solution</span>
                    </div>
                  </th>
                  <th className="py-4 px-6 text-center font-medium min-w-[200px]">
                    <div className="flex flex-col items-center">
                      <span>Generic Bitcoin SDK</span>
                      <span className="text-xs text-muted-foreground">Other solutions</span>
                    </div>
                  </th>
                  <th className="py-4 px-6 text-center font-medium min-w-[200px]">
                    <div className="flex flex-col items-center">
                      <span>Manual Implementation</span>
                      <span className="text-xs text-muted-foreground">DIY approach</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-4 px-6 text-left">MetaNet Protocol Support</td>
                  <td className="py-4 px-6 text-center text-green-500"><Check className="h-5 w-5 mx-auto" /></td>
                  <td className="py-4 px-6 text-center text-yellow-500">Partial</td>
                  <td className="py-4 px-6 text-center text-yellow-500">Partial</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-6 text-left">Identity Management</td>
                  <td className="py-4 px-6 text-center text-green-500"><Check className="h-5 w-5 mx-auto" /></td>
                  <td className="py-4 px-6 text-center text-yellow-500">Basic</td>
                  <td className="py-4 px-6 text-center text-red-500">No</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-6 text-left">Cross-platform Support</td>
                  <td className="py-4 px-6 text-center text-green-500"><Check className="h-5 w-5 mx-auto" /></td>
                  <td className="py-4 px-6 text-center text-yellow-500">Varies</td>
                  <td className="py-4 px-6 text-center text-red-500">No</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-6 text-left">Developer Experience</td>
                  <td className="py-4 px-6 text-center text-green-500">Excellent</td>
                  <td className="py-4 px-6 text-center text-yellow-500">Good</td>
                  <td className="py-4 px-6 text-center text-red-500">Complex</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-6 text-left">Ecosystem Integration</td>
                  <td className="py-4 px-6 text-center text-green-500"><Check className="h-5 w-5 mx-auto" /></td>
                  <td className="py-4 px-6 text-center text-yellow-500">Limited</td>
                  <td className="py-4 px-6 text-center text-red-500">No</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-left">Enterprise Support</td>
                  <td className="py-4 px-6 text-center text-green-500"><Check className="h-5 w-5 mx-auto" /></td>
                  <td className="py-4 px-6 text-center text-yellow-500">Varies</td>
                  <td className="py-4 px-6 text-center text-red-500">No</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-10 text-center">
            <Button 
              onClick={() => window.open('/docs/metanet-desktop/comparison', '_blank')}
              variant="outline"
            >
              View Detailed Comparison
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">
            Ready to build on MetaNet?
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Join the growing community of developers building innovative applications with Metanet Desktop
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              className="group"
              onClick={() => window.open('/docs/metanet-desktop/getting-started', '_blank')}
            >
              Start Building
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.open('https://discord.gg/babbage-metanet', '_blank')}
            >
              Join Developer Community
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

// Feature Card Component
const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => (
  <div 
    className="p-6 rounded-lg border bg-card hover:shadow-md transition-all duration-300 hover:translate-y-[-5px] cursor-pointer"
    onClick={() => window.open(`/docs/metanet-desktop/features/${title.toLowerCase().replace(/\s+/g, '-')}`, '_blank')}
  >
    <div className="text-primary mb-4">{icon}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
    <div className="mt-4 flex items-center text-primary text-sm font-medium">
      Learn more <ArrowRight className="ml-1 h-3 w-3" />
    </div>
  </div>
)

// Use Case Card Component
const UseCaseCard: React.FC<UseCaseCardProps> = ({ title, description, tag }) => (
  <div 
    className="p-6 rounded-lg border bg-card hover:shadow-md transition-all duration-300 hover:translate-y-[-5px] cursor-pointer"
    onClick={() => window.open(`/docs/metanet-desktop/use-cases/${title.toLowerCase().replace(/\s+/g, '-')}`, '_blank')}
  >
    <div className="mb-3">
      <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-primary/10 text-primary">
        {tag}
      </span>
    </div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
    <div className="mt-4 flex items-center text-primary text-sm font-medium">
      View details <ArrowRight className="ml-1 h-3 w-3" />
    </div>
  </div>
)

// Added type for ClipboardIcon props
const ClipboardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
  </svg>
)

export default MetanetDesktop 